/**
 * The arena-wide feed (Server-Sent Events on `/api/arena/events`): what every open page hears.
 * Records reach everyone and are replayed to late pages; presence is a live count; challenges
 * and quest completions are addressed to one player.
 */

import { z } from 'zod'
import type { GameId } from './duel/protocol'
import type { PlayerPublic } from './protocol'

export type CpuRecordKind = 'longest' | 'draw' | 'win'

interface ArenaEventBase {
  /** Unique per event; clients dedupe on it. */
  id: string
  at: number
}

/** A human did something notable against a house bot. */
export interface CpuRecordEvent extends ArenaEventBase {
  type: 'cpuRecord'
  /** Which house bot the record is against. */
  game: GameId
  kind: CpuRecordKind
  player: PlayerPublic
  moves: number
  /** `longest` only: the record that was beaten (0 = first game ever). */
  previous?: number
}

/** How many players have the arena open right now (hidden players excluded). */
export interface PresenceEvent extends ArenaEventBase {
  type: 'presence'
  online: number
}

/** The games a player can be challenged to: every multiplayer game. */
export const CHALLENGE_GAMES = ['xox', 'c4', 'cycles', 'ice'] as const
export type ChallengeGame = (typeof CHALLENGE_GAMES)[number]

/** "Come play": a room already exists with the challenger in it. */
export interface ChallengeEvent extends ArenaEventBase {
  type: 'challenge'
  to: string
  from: PlayerPublic
  game: ChallengeGame
  code: string
  expiresAt: number
}

/** One of today's quests just completed for this player. */
export interface QuestDoneEvent extends ArenaEventBase {
  type: 'questDone'
  to: string
  quest: { id: string; title: string }
  xp: number
}

export type ArenaEvent = CpuRecordEvent | PresenceEvent | ChallengeEvent | QuestDoneEvent
/** Events addressed to one player rather than the whole arena. */
export type TargetedArenaEvent = ChallengeEvent | QuestDoneEvent

export const challengeBodySchema = z.object({
  to: z.string().min(1).max(64),
  game: z.enum(CHALLENGE_GAMES),
})
export type ChallengeBody = z.infer<typeof challengeBodySchema>

export interface OnlinePlayer extends PlayerPublic {
  level: number
}

/** `GET /api/arena/online`: who is here right now, hidden players left out. */
export interface OnlineResponse {
  players: OnlinePlayer[]
}

/** A challenge card that is not accepted within this long disappears. */
export const CHALLENGE_TTL_MS = 60_000
/** A player whose presence was not refreshed within this long has left. */
export const PRESENCE_FRESH_MS = 60_000
/** How long the record banner stays on screen. */
export const ARENA_NOTICE_MS = 9_000
/** A replayed event older than this is stale and shown to nobody. */
export const ARENA_REPLAY_MS = 120_000
